import { I18n } from '@calendly/i18n';
import { Avatar } from '@calendly/ui/components/avatar';
import { BareButton } from '@calendly/ui/components/bare-button';
import { FullStoryExclude, FullStoryMask } from '@calendly/ui/components/fullstory-privacy';
import { IconChevronDown } from '@calendly/ui/components/glyphs/IconChevronDown';
import { IconChevronUp } from '@calendly/ui/components/glyphs/IconChevronUp';
import { Loader } from '@calendly/ui/components/loader';
import { BodyText } from '@calendly/ui/components/typography';
import { UiIcon } from '@calendly/ui/components/ui-icon';
import { colorTextDestructive, colorTextSecondary } from '@calendly/ui/theme';
import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { RemoteContactsSearchHandler } from 'features/crm';
import { useRightRail } from 'features/right_rail';
import { useTrack } from 'hooks/analytics_events';
import { getInitials } from 'shared/utils';
import { AttendeeDetailsInline } from './base_attendee/attendee_details_inline';
import { useResolvedAttendeeDetails } from './base_attendee/use_resolved_attendee_details';
import CancelledIcon from './cancel.svg';
import { InviteeActions } from './invitee_actions';
import { AvatarContainer, AvatarIcon, Container, Heading, NameContainer, NameString, inviteeDetailsInCardsStackCss, avatarContainerFixedSizeCss, Wrapper } from './styles';
const expandableNameCss = "e1wby5d3";
const nameContainerExpandableCss = "n6lr8sd";
const chevronClusterCss = "c1dd7a5";
const soloActionsRowCss = "s1xj02hq";
const expandedBodyCss = "edfp11y";

/** Skips CRM email search for hosts (no lookup by email for meeting owner rows). */
const CrmHostContactSearchSkip = ({
  onResults,
  onFetching
}) => {
  useEffect(() => {
    onFetching?.(false);
    onResults?.([]);
  }, [onResults, onFetching]);
  return null;
};
const CrmExternalAttendeeCard = ({
  event,
  invitee,
  isExpanded,
  onToggle,
  panelKey,
  nameSource = 'contact'
}) => {
  const {
    resolved,
    isLoading,
    contact,
    handleContactResults,
    handleContactFetching
  } = useResolvedAttendeeDetails(invitee, {
    nameSource
  });
  const history = useHistory();
  const {
    close
  } = useRightRail();
  const track = useTrack();
  const cancelled = invitee.rsvp === 'declined';
  const isHost = invitee.kind === 'HOST';
  const headerEmail = resolved?.email ?? invitee.email;
  const showContactDropdown = Boolean(contact && !isHost);
  const canExpand = Boolean(contact) && !isHost;
  const contactId = resolved?.contactId ?? contact?.id ?? contact?.uuid ?? null;
  const sendAnalytics = (action, contactUuid) => {
    track({
      name: 'Contact Interaction',
      properties: {
        action: action || 'profile_view',
        location: 'meetings_page_right_rail',
        contact_uuid: contactUuid ?? contactId ?? invitee.uuid
      }
    });
  };
  const handleCrmNameClick = e => {
    e.stopPropagation();
    if (!contact || !contactId || isHost) return;
    close();
    history.push(`/app/contacts/${contactId}`);
    sendAnalytics('contact_details_view', contactId);
  };
  const handleHeaderKeyDown = e => {
    if (!canExpand) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onToggle?.();
    }
  };
  const renderName = () => {
    if (showContactDropdown) {
      return <NameString as={BareButton} type="button" strike={cancelled} bold className={expandableNameCss} onClick={handleCrmNameClick}>
          <FullStoryMask>{resolved?.name}</FullStoryMask>
        </NameString>;
    }
    return <NameString strike={cancelled} bold className={expandableNameCss}>
        <FullStoryMask>{headerEmail}</FullStoryMask>
      </NameString>;
  };
  return <Wrapper isGroup isExpanded={canExpand && isExpanded} data-testid={`external-attendee-${panelKey}`}>
      <FullStoryExclude>
        {isHost ? <CrmHostContactSearchSkip onResults={handleContactResults} onFetching={handleContactFetching} /> : <RemoteContactsSearchHandler emails={invitee.email ? [invitee.email] : undefined} onResults={handleContactResults} onFetching={handleContactFetching} />}
      </FullStoryExclude>

      {isLoading && <Loader data-testid="contact-loader" size="small" />}

      {!isLoading && resolved && <>
          <Container isGroup isOpen={canExpand && isExpanded} noBottomMargin clickable={canExpand} onClick={canExpand ? onToggle : undefined} onKeyDown={canExpand ? handleHeaderKeyDown : undefined} aria-expanded={canExpand ? isExpanded : undefined} role={canExpand ? 'button' : undefined} tabIndex={canExpand ? 0 : undefined}>
            <AvatarContainer className={avatarContainerFixedSizeCss}>
              <FullStoryExclude>
                <Avatar letter={getInitials(showContactDropdown ? resolved.name : headerEmail)} src={showContactDropdown ? resolved.avatarUrl : undefined} alt={showContactDropdown ? resolved.name : headerEmail} aria-label={showContactDropdown ? resolved.name : headerEmail} size="small" />
                {cancelled && <AvatarIcon src={CancelledIcon} />}
              </FullStoryExclude>
            </AvatarContainer>
            <NameContainer className={nameContainerExpandableCss}>
              <Heading isContact={false}>
                {renderName()}
                {isHost && <BodyText size="x-small" color={colorTextSecondary}>
                    {I18n.t('publisher.meetings.invitees.meeting_owner')}
                  </BodyText>}
                {cancelled && !isHost && <BodyText size="x-small" color={colorTextDestructive} weight="semibold">
                    {I18n.t('publisher.meetings.header_badge.canceled')}
                  </BodyText>}
              </Heading>
            </NameContainer>
            {canExpand && <span className={chevronClusterCss}>
                <BodyText size="x-small" color={colorTextSecondary} as="span">
                  {I18n.t('publisher.meetings.invitees.detail')}
                </BodyText>
                {isExpanded ? <UiIcon icon={IconChevronUp} size="small" /> : <UiIcon icon={IconChevronDown} size="small" />}
              </span>}
          </Container>
          {canExpand && isExpanded && <div className={`${inviteeDetailsInCardsStackCss} ${expandedBodyCss}`}>
              <AttendeeDetailsInline resolved={resolved} canEditEmail={false} />
              <div className={soloActionsRowCss}>
                <InviteeActions event={event} invitee={invitee} resolved={resolved} sendAnalytics={sendAnalytics} showPrimaryActions canMarkNoShow={false} canReschedule={false} canCancel={false} canBlock={false} canUnblock={false} canEditEmail={false} hideKebabMenu />
              </div>
            </div>}
        </>}
    </Wrapper>;
};
export const ExternalAttendeeCard = CrmExternalAttendeeCard;

require("./external_attendee_card.wyw-in-js.css!=!../../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./external_attendee_card.js");